import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import { CheckCircle2, Circle, Rocket, X, ArrowRight } from "lucide-react";

const DISMISS_KEY = "twp_ob_dismissed";
const EXPLORE_KEY = "twp_ob_explored";

function scrollToTestid(testid) {
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => { try { el.focus({ preventScroll: true }); } catch (e) {} }, 400);
  }
}

export function OnboardingChecklist({ balance = 0, serviceSelected, linkAdded, qtyValid, ordersCount = 0, ready = true }) {
  const { t } = useContent();
  const nav = useNavigate();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [explored, setExplored] = useState(() => localStorage.getItem(EXPLORE_KEY) === "1");

  // New clients only: never show once they have an order, before data is ready, or if dismissed.
  if (!ready || dismissed || ordersCount > 0) return null;

  const markExplored = () => { localStorage.setItem(EXPLORE_KEY, "1"); setExplored(true); };
  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); };

  const steps = [
    { key: "funds", label: t("client.ob.funds", "Ajouter des fonds à votre solde"), done: balance > 0,
      cta: t("client.ob.funds_cta", "Ajouter des fonds"), onClick: () => nav("/dashboard/add-funds") },
    { key: "explore", label: t("client.ob.explore", "Explorer le catalogue de services"), done: explored,
      cta: t("client.ob.explore_cta", "Voir le catalogue"), onClick: () => { markExplored(); nav("/dashboard/services"); } },
    { key: "service", label: t("client.ob.service", "Choisir une plateforme, catégorie et service"), done: !!serviceSelected,
      cta: t("client.ob.service_cta", "Choisir un service"), onClick: () => scrollToTestid("order-platform") },
    { key: "link", label: t("client.ob.link", "Coller le lien de votre publication"), done: !!linkAdded,
      cta: t("client.ob.link_cta", "Ajouter le lien"), onClick: () => scrollToTestid("order-link") },
    { key: "quantity", label: t("client.ob.quantity", "Définir la quantité souhaitée"), done: !!qtyValid,
      cta: t("client.ob.quantity_cta", "Définir la quantité"), onClick: () => scrollToTestid("order-quantity") },
    { key: "order", label: t("client.ob.order", "Soumettre votre première commande"), done: ordersCount > 0,
      cta: t("client.ob.order_cta", "Soumettre"), onClick: () => scrollToTestid("submit-order-btn") },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);
  // Fully complete without an order yet: collapse gracefully (all set, ready to submit).
  const allDone = doneCount === steps.length;

  return (
    <div data-testid="onboarding-checklist" className="rounded-2xl border border-accent/30 bg-accent/5 p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 hidden h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent sm:flex">
          <Rocket className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-display text-base font-bold">{t("client.ob.title", "Bien démarrer")}</h2>
            <button data-testid="onboarding-dismiss" onClick={dismiss}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
              <X className="h-3.5 w-3.5" /> {t("client.ob.dismiss", "Ne plus afficher")}
            </button>
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {allDone ? t("client.ob.almost", "Tout est prêt — il ne reste plus qu'à soumettre votre commande !")
              : t("client.ob.subtitle", "Suivez ces étapes pour passer votre première commande en quelques minutes.")}
          </p>

          {/* progress */}
          <div className="mt-3 flex items-center gap-3">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
              <div data-testid="onboarding-progress-bar" className="h-full rounded-full bg-accent transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <span data-testid="onboarding-progress" className="shrink-0 text-xs font-semibold text-accent">{doneCount}/{steps.length}</span>
          </div>

          {/* steps */}
          <ul className="mt-4 space-y-2.5">
            {steps.map((s) => (
              <li key={s.key} data-testid={`onboarding-step-${s.key}`} className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                {s.done
                  ? <CheckCircle2 data-testid={`onboarding-step-${s.key}-done`} className="h-5 w-5 shrink-0 text-emerald-500" />
                  : <Circle className="h-5 w-5 shrink-0 text-muted-foreground/50" />}
                <span className={`min-w-0 flex-1 basis-[55%] text-sm ${s.done ? "text-muted-foreground line-through" : "font-medium"}`}>{s.label}</span>
                {!s.done && (
                  <button data-testid={`onboarding-step-${s.key}-cta`} onClick={s.onClick}
                    className="ml-8 inline-flex min-h-9 shrink-0 items-center gap-1 rounded-full bg-accent/15 px-3.5 py-2 text-xs font-semibold text-accent transition-colors hover:bg-accent/25 sm:ml-0">
                    {s.cta} <ArrowRight className="h-3 w-3" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
